"use client";
import React, { useContext } from "react";
import { ActionIcon, Box, Loader, Text } from "rizzui";
import formatDate from "../lib/utils/formatDate";
import {
  useCallDetails,
  useCallProviderDetails,
} from "../hooks/useCallDetails";
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import { useDrawer } from "../hooks/useDrawer";
import CloseIcon from "../assets/close";
import { parseEvaluation } from "../lib/utils/parse";
import DetailsTabContent from "./detailsTabContent";
import TranscriptTabContent from "./transcriptTabContent";
import EvaluationTabContent from "./evaluationTabContent";
import { AuthContext } from "../context/authContext";

export default function CallDrawer({ row }) {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const { profile } = useContext(AuthContext);
  const { isLoading, data, error } = useCallDetails({
    id: row?.original?._id,
    location: "calls",
  });

  const {
    isLoading: providerLoading,
    data: providerData,
    error: providerError,
  } = useCallProviderDetails({
    id: data?.call_platform_id,
  });

  const { closeDrawer } = useDrawer();

  const transcript = providerData?.messages?.filter(
    (message) => message?.role === "bot" || message?.role === "user"
  );

  const evaluation = providerData?.analysis?.successEvaluation;

  const successEvaluation = parseEvaluation(evaluation);
  const details = (
    <div className="flex flex-col gap-[11px]">
      <Text className="font-switzer text-center font-[500] text-[18px] leading-[23.76px]">
        {row?.original?.lead_details?.firstname}
      </Text>
      <Text className="font-switzer text-center font-[400] text-[14px] leading-[15.84px]">
        {row?.original?.phone_number}
      </Text>
    </div>
  );

  const activityDetails = (
    <Box className="w-full flex flex-col gap-[24px]">
      <div className="flex flex-row justify-between">
        <Text className="font-switzer font-[500] text-[14px] leading-[18.48px]">
          Date & time called
        </Text>
        <Text className="font-switzer font-[400] dark:text-[#758599] text-[12px] leading-[15.84px]">
          {isLoading ? (
            <Loader variant="threeDot" />
          ) : (
            <span className="text-[#758599]">
              {formatDate(data?.updatedAt) || "--"}
            </span>
          )}
        </Text>
      </div>
      {/*  <div className="flex flex-row justify-between">
        <Text className="font-switzer font-[500] text-[14px] leading-[18.48px]">
          Reason call ended
        </Text>
        <Text className="font-switzer font-[400] dark:text-[#758599] text-[12px] leading-[15.84px]">
          {data?.call_end_reason?.replaceAll("-", " ") ?? "--"}
        </Text>
      </div> */}
      <div className="flex flex-row justify-between">
        <Text className="font-switzer font-[500] text-[14px] leading-[18.48px]">
          Status
        </Text>
        <Text className="font-switzer font-[400] dark:text-[#758599] text-[12px] leading-[15.84px]">
          {data?.call_status === "failed"
            ? data?.call_retry_count < 3
              ? "failed, pending reschedule"
              : "failed"
            : data?.call_status}
        </Text>
      </div>
      <div className="flex flex-row justify-between">
        <Text className="font-switzer font-[500] text-[14px] leading-[18.48px]">
          Duration
        </Text>
        <Text className="font-switzer font-[400] dark:text-[#758599] text-[12px] leading-[15.84px]">
          {data?.call_duration_minutes !== undefined &&
          data?.call_duration_minutes !== null
            ? `${data.call_duration_minutes.toFixed(2)} min`
            : "--"}
        </Text>
      </div>

      <div className="flex flex-row justify-between">
        <Text className="font-switzer font-[500] text-[14px] leading-[18.48px]">
          Cost
        </Text>
        <Text className="font-switzer font-[400] dark:text-[#758599] text-[12px] leading-[15.84px]">
          {data?.call_cost !== undefined && data?.call_cost !== null
            ? `${profile?.user_currency_symbol ?? ""}${data.call_cost.toFixed(
                2
              )}`
            : "--"}
        </Text>
      </div>
    </Box>
  );

  const tabClass =
    "text-center text-[16px] h-[50px] font-[400] !outline-none data-[selected]:border-b-[3px] data-[selected]:font-[500] data-[selected]:border-primary-dark text-primary-dark";
  return (
    <>
      <div className="dark:bg-[#2A2C30] text-primary-dark flex flex-col justify-start gap-[16px] md:gap-[32px] backdrop-blur-lg h-screen p-[20px] !pb-[10px]">
        <ActionIcon
          variant="text"
          onClick={closeDrawer}
          className={"text-primary-dark p-0"}
          rounded="full"
        >
          <CloseIcon className={"text-primary-dark"} />
        </ActionIcon>
        <div>
          <Text className="font-ibmPlexSerif text-[18px] leading-[23.4px] font-[600]">
            Call details
          </Text>
        </div>

        <div className="w-full flex flex-col h-[179px] border-[.5px] dark:bg-[#333F48] dark:border-0 rounded-[8px] border-[#D9D9D9] justify-center items-center gap-[10px]">
          <Box className="bg-[#D9D9D9] rounded-full h-[72px] w-[72px]" />
          {details}
        </div>

        <div className="flex-1 flex flex-col overflow-hidden">
          <TabGroup
            selectedIndex={selectedIndex}
            onChange={setSelectedIndex}
            className={"h-full"}
          >
            <TabList
              className={
                "items-center w-full justify-between flex font-switzer text-[16px] mb-[20px]"
              }
            >
              <Tab className={`${tabClass} flex-[0.8]`}>Details</Tab>
              <Tab className={`${tabClass} flex-1`}>Transcript</Tab>
              <Tab className={`${tabClass} flex-[1.2]`}>Success evaluation</Tab>
            </TabList>
            <TabPanels className="overflow-y-auto scroll-smooth customBar h-[calc(100%-70px)]">
              <TabPanel static>
                <div hidden={selectedIndex !== 0}>
                  <DetailsTabContent
                    isLoading={isLoading}
                    providerLoading={providerLoading}
                    activityDetails={activityDetails}
                    providerData={providerData}
                    data={data}
                  />
                </div>
              </TabPanel>
              <TabPanel static>
                <div hidden={selectedIndex !== 1}>
                  <TranscriptTabContent
                    transcript={transcript}
                    row={row}
                    callStatus={data?.call_status}
                  />
                </div>
              </TabPanel>
              <TabPanel static>
                <div hidden={selectedIndex !== 2}>
                  <EvaluationTabContent
                    successEvaluation={successEvaluation}
                    callStatus={data?.call_status}
                  />
                </div>
              </TabPanel>
            </TabPanels>
          </TabGroup>
        </div>

        {/*         <div className="flex-1 flex flex-col overflow-hidden">
          <div className={"h-full"}>
            <div className={"overflow-y-auto scroll-smooth customBar h-full"}>
              <div>
                {isLoading || providerLoading ? (
                  <div className="flex-1">
                    <MyLoader />
                  </div>
                ) : (
                  <Box className="flex flex-col gap-[32px] px-[12px]">
                    {activityDetails}
                    <Box>
                      <Text className="font-switzer font-[500] text-[14px] leading-[18.48px]">
                        Call summary
                      </Text>
                      <Text className="font-switzer font-[400] dark:text-[#758599] text-[12px] leading-[15.84px]">
                        {providerData?.summary ||
                          "The summary of the call will appear here once the call is completed."}
                      </Text>
                    </Box>

                    <Box>
                      <Text className="font-switzer font-[500] text-[14px] leading-[18.48px] mb-[16px]">
                        Recording
                      </Text>
                      {data?.recording_url ? (
                        <CallRecording recordingUrl={data?.recording_url} />
                      ) : (
                        <Text className="font-switzer font-[400] dark:text-[#758599] text-[12px] leading-[15.84px]">
                          {data?.call_status === "completed"
                            ? "Call recording is not available."
                            : "The call recording will appear here."}
                        </Text>
                      )}
                    </Box>
                  </Box>
                )}
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </>
  );
}
